import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarHeart, ArrowRight, Brain, Clock, Play, StickyNote, Quote } from "lucide-react";
import { FadeUp, MaskedLines, Stagger, StaggerItem } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { WatchListen } from "@/components/WatchListen";
import { TESTIMONIAL_META, PAIN_IDS, CREDENTIALS, formatDate } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const PAIN_ICONS = { time: Clock, initiation: Play, memory: StickyNote };

const Hero = () => {
  const { t, to } = useLang();
  const h = t.home;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-24">
      <div className="absolute top-0 right-0 w-[42rem] h-[42rem] rounded-full bg-sand blur-3xl opacity-70 -translate-y-1/3 translate-x-1/4 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full bg-sage-soft text-sage-dark text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 mb-8"
          >
            <Brain className="w-4 h-4" /> {h.heroBadge}
          </motion.p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-balance">
            <MaskedLines lines={[h.heroL1, h.heroL2, h.heroL3]} />
            <span className="block overflow-hidden pb-2 -mb-2 mt-3">
              <motion.span
                className="block italic text-terracotta"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.62, ease: [0.22, 1, 0.36, 1] }}
              >
                {h.heroAccent}
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 text-base sm:text-lg text-clay leading-relaxed max-w-xl"
          >
            {h.heroSubPre}
            <span className="font-semibold text-espresso">{h.heroSubStrong}</span>
            {h.heroSubPost}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link
              to={to("/booking")}
              data-testid="hero-book-discovery-btn"
              className="inline-flex items-center gap-2.5 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5 shadow-[0_16px_36px_-14px_rgba(200,109,81,0.6)]"
            >
              <CalendarHeart className="w-5 h-5" />
              {h.heroCtaPrimary}
            </Link>
            <Link
              to={to("/services")}
              data-testid="hero-explore-services-btn"
              className="inline-flex items-center gap-2 rounded-full border border-sage text-sage-dark px-8 py-4 text-base font-semibold transition-colors duration-200 hover:bg-sage-soft"
            >
              {h.heroCtaSecondary}
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-5 text-sm text-taupe"
          >
            {h.heroMicro}
          </motion.p>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            style={{ y: isDesktop ? y : 0 }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-md mx-auto"
          >
            <div className="absolute -inset-4 rounded-t-[10rem] rounded-b-[2rem] border border-terracotta/25 pointer-events-none" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-t-[9rem] rounded-b-3xl bg-cream shadow-[0_40px_80px_-40px_rgba(44,37,32,0.45)]">
              <img
                src="/images/natalia-portrait.jpg"
                alt="Natalia Peña, ADHD and executive function life coach"
                data-testid="hero-portrait"
                className="w-full h-[26rem] sm:h-[30rem] object-cover object-[50%_22%]"
              />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 z-10 bg-white rounded-2xl shadow-[0_20px_50px_-20px_rgba(44,37,32,0.4)] px-5 py-4 flex items-center gap-3">
              <a
                href="https://www.credly.com/badges/1e8e9a99-f846-4566-a333-ecab8137cf2c/public_url"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-icf-badge"
                aria-label="ICF Member credential on Credly"
                className="shrink-0 transition-transform duration-200 hover:scale-105"
              >
                <img
                  src={CREDENTIALS[0].img}
                  alt={CREDENTIALS[0].name}
                  className="w-11 h-11 rounded-full object-contain bg-badgebg"
                />
              </a>
              <div>
                <p className="text-sm font-semibold">Natalia Peña</p>
                <p className="text-xs text-taupe">{h.portraitRole}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const PainPoints = () => {
  const { t } = useLang();
  const h = t.home;
  return (
    <section data-testid="pain-points-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
      <FadeUp className="max-w-2xl mb-14">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{h.painKicker}</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-balance">{h.painTitle}</h2>
        <p className="mt-5 text-base sm:text-lg text-clay leading-relaxed">{h.painIntro}</p>
      </FadeUp>
      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {PAIN_IDS.map((id) => {
          const p = h.pain[id];
          const Icon = PAIN_ICONS[id];
          return (
            <StaggerItem key={id}>
              <div
                data-testid={`pain-card-${id}`}
                className="h-full rounded-3xl bg-white border border-border p-8 shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)] transition-transform duration-300 hover:-translate-y-1"
              >
                <span className="w-12 h-12 rounded-2xl bg-terracotta-soft flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-terracotta" />
                </span>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold">{p.title}</h3>
                <p className="mt-3 text-sm text-clay leading-relaxed">{p.struggle}</p>
                <div className="mt-5 pt-5 border-t border-border">
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-sage-dark mb-2">{h.painHelpLabel}</p>
                  <p className="text-sm text-espresso leading-relaxed font-medium">{p.reframe}</p>
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
};

const Chapters = () => {
  const { t } = useLang();
  const h = t.home;
  return (
    <section data-testid="chapters-section" className="bg-cream/70 border-y border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
        <FadeUp className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{h.chaptersKicker}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{h.chaptersTitle}</h2>
          <p className="mt-5 text-base sm:text-lg text-clay leading-relaxed">{t.bio.approach}</p>
        </FadeUp>
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.chapters.map((c) => (
            <StaggerItem key={c.num}>
              <div data-testid={`chapter-${c.num}`} className="h-full rounded-3xl bg-linen border border-border p-7">
                <p className="font-serif text-5xl text-terracotta/40 font-semibold">{c.num}</p>
                <h3 className="font-serif text-xl font-semibold mt-4">{c.title}</h3>
                <p className="mt-3 text-sm text-clay leading-relaxed">{c.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

const TestimonialsPreview = () => {
  const { t, to, lang } = useLang();
  const h = t.home;
  return (
    <section data-testid="testimonials-preview-section" className="bg-cream/70 border-y border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-24">
        <FadeUp className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{h.quotesKicker}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{h.quotesTitle}</h2>
        </FadeUp>
        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {TESTIMONIAL_META.map((m) => {
            const td = t.testimonialsData[m.id];
            return (
              <StaggerItem key={m.id}>
                <figure
                  data-testid={`home-testimonial-${m.id}`}
                  className="h-full flex flex-col rounded-3xl bg-white border border-border p-8 shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)]"
                >
                  <Quote className="w-7 h-7 text-terracotta/50 mb-5" />
                  <blockquote className="flex-1 text-base text-espresso leading-relaxed font-medium">
                    &ldquo;{td.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-7 pt-6 border-t border-border flex items-center gap-4">
                    <img src={m.avatar} alt={m.name} className="w-12 h-12 rounded-full object-cover bg-cream" loading="lazy" />
                    <div>
                      <p className="text-sm font-semibold">{m.name}</p>
                      <p className="text-xs text-taupe">
                        {td.catLabel} · {formatDate(m.date, lang)}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              </StaggerItem>
            );
          })}
        </Stagger>
        <FadeUp className="mt-10 text-center">
          <Link
            to={to("/testimonials")}
            data-testid="testimonials-preview-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:text-terracotta-dark transition-colors"
          >
            {h.quotesLink} <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <PainPoints />
      <Chapters />
      <WatchListen />
      <TestimonialsPreview />
    </>
  );
}
